/* eslint-disable rxjs/no-subscribe-handlers */
import { AfterViewInit, ChangeDetectorRef, Component, HostListener, Injector, isDevMode, NgZone, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Message, MessagingService, TrackingService } from '@ca/core/services';
import { getLeftNumberOfDays, getNewDate } from '@ca/utils';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { OAuthService } from 'angular-oauth2-oidc';
import browserUpdate from 'browser-update';
import QuillNamespace from 'quill';
import QuillImageDropAndPaste from 'quill-image-drop-and-paste';
import ImageResize from 'quill-resize-image';
import { BehaviorSubject, combineLatest, EMPTY, Subject } from 'rxjs';
import { catchError, delay, filter, map, pairwise, switchMap, tap } from 'rxjs/operators';
/**zone.js v0.14.4: Deep and legacy dist/ imports like zone.js/bundles/zone-testing.js and zone.js/dist/task-tracking are no longer allowed.
zone-testing-bundle and zone-testing-node-bundle are also no longer part of the package. The proper way to import zone.js is import 'zone.js' or;
import 'zone.js/testing';*/
import 'zone.js';
import { environment } from '../environments/environment';
import { isInDisApeer, isInRunner, isInWorkflowEditor } from '../utils/route/route-utils';
import { AppFacadeService } from './app-facade.service';
import { LoadingState, UserLimit, UserLimitKind, UserSubscription } from './core/models';
import { InlineManualTracking } from './core/models/tutorial.model';
import { UserInfo } from './core/models/user-info.model';
import { BreakpointObserverService } from './core/services/breakpoint-observer.service';
import { DatasetNotificationService } from './core/services/dataset-notification.service';
import { ExperimentalFeaturesService } from './core/services/experimental-features.service';
import { FileUploadService } from './core/services/file-upload.service';
import { MetaService } from './core/services/meta.service';
import { RoutingStateService } from './core/services/routing-state.service';
import { TutorialService } from './core/services/tutorial.service';
import { UiThemeService } from './core/services/ui-theme.service';
import { UserNotificationService } from './core/services/user-notification.service';
import { UserWorkspaceService } from './core/services/user-workspace.service';
import { UserService } from './core/services/user.service';
import { WindowRef } from './core/services/window.service';
import { WorkflowRunQueueService } from './core/services/workflow-run-queue.service';
import { SubscriptionFacadeService } from './features/user/services/subscription-facade/subscription-facade.service';
import { LIMITS_TOPIC } from './shared-modules/notifications-shared/services/notifications.service';
import { ImageFormat } from './shared/components/quill-editor-wrapper/quill-image-format';
import { sidebarAutoCollapseWidth } from './shared/constants/constants';
import { FullNamePipe } from './shared/pipes/full-name/full-name.pipe';

const Quill: typeof QuillNamespace = QuillNamespace;
window['Quill'] = Quill;
Quill.register('modules/ImageResize', ImageResize);
Quill.register('modules/imageDropAndPaste', QuillImageDropAndPaste);
Quill.register(ImageFormat, true);

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    providers: [SubscriptionFacadeService],
})
export class AppComponent implements AfterViewInit, OnInit {
    readonly _sidebarAutoCollapseWidth = sidebarAutoCollapseWidth;

    isSmallDevice: boolean | undefined;

    // WARNING: The sidebar needs to be closed by default to work correctly in logged out mode
    isSidebarOpened = false; // NEVER CHANGE THIS VALUE! IT HAS ALWAYS TO BE FALSE!!!

    isSideBarCollapsed = false; // true if sidebar is auto-collapsed by the autoCollapseWidth HTML attribute

    get isLoggedIn(): boolean {
        return this.userService.isLoggedIn;
    }

    // We hide sidebar if we are not logged in yet or on larger devices in logged out mode
    get showSidebar(): boolean | undefined {
        return this.userService.isLoggedIn || this.isSmallDevice;
    }

    // Overlay sidebar on small device instead of pushing the content aside
    get sidebarMode(): 'over' | 'push' {
        return this.isSmallDevice ? 'over' : 'push';
    }

    get userInfo(): UserInfo | undefined {
        return this.userService.myInfo;
    }

    get showFullscreen(): boolean {
        return isInRunner(this.router.url) || isInWorkflowEditor(this.router.url);
    }

    private viewLoaded$$ = new Subject();
    private userSubscriptionState$ = this.userService.userSubscriptionState$;
    // eslint-disable-next-line rxjs/no-explicit-generics
    private userSubscriptionSubject$$ = new BehaviorSubject<UserSubscription | null>(null);
    userSubscription$ = this.userSubscriptionSubject$$.asObservable();
    isMEDProjectEnabled = this.experimentalFeaturesService.isMEDProjectActive;

    constructor(
        private appRootFacadeService: AppFacadeService,
        private breakpointObserverService: BreakpointObserverService,
        private changeDetectorRef: ChangeDetectorRef,
        private experimentalFeaturesService: ExperimentalFeaturesService,
        private fileUploadService: FileUploadService,
        private fullNamePipe: FullNamePipe,
        private injector: Injector,
        private oAuthService: OAuthService,
        private router: Router,
        private routingStateService: RoutingStateService,
        private translateService: TranslateService,
        private tutorialService: TutorialService,
        private uiThemeService: UiThemeService,
        private userNotificationService: UserNotificationService,
        private userWorkspaceService: UserWorkspaceService,
        private windowRef: WindowRef,
        private metaService: MetaService,
        private workflowRunQueueService: WorkflowRunQueueService,
        private messagingService: MessagingService,
        private mixpanelTrackingService: TrackingService,
        private subscriptionFacadeService: SubscriptionFacadeService,
        protected modalService: NgbModal,
        protected userService: UserService,
        protected datasetNotificationService: DatasetNotificationService,
    ) {
        this.routingStateService.loadRouting();
        this.uiThemeService.determineAndSetTheme();

        // this language will be used as a fallback when a translation isn't found in the current language
        this.translateService.setDefaultLang('en-US');

        // the lang to use, if the lang isn't available, it will use the current loader to get them
        // eslint-disable-next-line rxjs/no-ignored-observable
        this.translateService.use('en-US');

        if (!(isDevMode() || isInDisApeer(this.router.url))) {
            // outdated browser notification
            browserUpdate({
                required: {
                    i: false,
                },
                insecure: true,
                reminder: 0,
                l: 'en',
            });
        }

        const subscription = combineLatest([this.viewLoaded$$, this.userSubscriptionState$])
            .pipe(
                tap(([, userSubscriptionState]) => {
                    if (userSubscriptionState.loadingState === LoadingState.LOADED) {
                        // InlineManual script is conditionally added in main.ts depending on environment
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        if ((window as any).InlineManualPlayer) {
                            if (this.userService.myInfo) {
                                const { sub: uid, email } = this.userService.myInfo;
                                const trackingData: InlineManualTracking = {
                                    uid,
                                    email,
                                    username: this.fullNamePipe.transform(this.userService.myInfo),
                                    plan: userSubscriptionState.data?.name,
                                };
                                this.tutorialService.setTracking(trackingData);
                                this.tutorialService.createPlayer();
                                subscription.unsubscribe(); // unsubscribe as it should only happen once
                            }
                        }
                    }
                }),
            )
            .subscribe();
    }

    evaluateSubscriptionEndDate(): void {
        const localStorageKey = 'trial_period_ends_today_modal_shown';
        combineLatest([
            this.userService.userSubscriptionState$.pipe(
                filter(state => state.loadingState === LoadingState.LOADED),
                map(state => state.data),
            ),
            // eslint-disable-next-line rxjs/no-explicit-generics
            this.messagingService.subscription(LIMITS_TOPIC, new BehaviorSubject<Message<Map<UserLimitKind, UserLimit | string>> | null>(null)).pipe(
                delay(2000),
                filter((msg: Message<Map<UserLimitKind, UserLimit | string>>) => msg !== null),
                map((msg: Message<Map<UserLimitKind, UserLimit | string>>) => msg.payload),
                map(
                    (limits: Map<UserLimitKind, UserLimit | string>) =>
                        getLeftNumberOfDays(getNewDate().startOf('day'), getNewDate(limits.get('SubscriptionEndDate') as string).startOf('day')) === 0,
                ),
                filter(hasExpiredToday => {
                    if (!hasExpiredToday && localStorage.getItem(localStorageKey)) localStorage.removeItem(localStorageKey);
                    return hasExpiredToday;
                }),
            ),
        ])
            .pipe(
                tap(([subscription, hasExpiredToday]: [UserSubscription, boolean]) => {
                    if (hasExpiredToday && !localStorage.getItem(localStorageKey)) {
                        this.subscriptionFacadeService.requestSubscriptionOffer(subscription, hasExpiredToday);
                        localStorage.setItem(localStorageKey, JSON.stringify(true));
                    }
                }),
            )
            .subscribe();
    }

    @HostListener('window:beforeunload', ['$event']) canLeavePageIfNoUploadIsInProgress($event: BeforeUnloadEvent): BeforeUnloadEvent | undefined {
        if (this.fileUploadService.isAnyUploadInProgress()) {
            // 424249: Opens a confirm message. Attention: It's not possible to enter a custom message here,
            // because the browsers stopped to pick it up a while ago. We just put it there in case the browsers change their implementation.
            // Reference: https://stackoverflow.com/questions/38879742/is-it-possible-to-display-a-custom-message-in-the-beforeunload-popup
            $event.returnValue = 'You have some unsaved changes';
            $event.preventDefault();

            return $event;
        }
    }

    ngOnInit(): void {
        this.metaService.updateFavicon();

        if (isDevMode() && environment.ngZoneDebugging) {
            this.initDebugNgZone();
        }
        if (this.userService.isLoggedIn) {
            // if user does not have user_service granted scope user token is the old one and we need to log out him
            // so that he logs in again and gets a new token with user_service granted scope
            if (
                this.oAuthService.getGrantedScopes() != null &&
                Array.isArray(this.oAuthService.getGrantedScopes()) &&
                !(this.oAuthService.getGrantedScopes() as Array<string>).includes('user_service')
            ) {
                this.oAuthService.revokeTokenAndLogout();
            }
            this.userService.getPermissions$().subscribe();
            this.userService.refreshUserImages$().subscribe();
            this.userService.permissionsError$
                .pipe(
                    switchMap(error => {
                        if (error) {
                            return this.translateService.get('GENERAL.PERMISSIONS_LOAD_ERROR');
                        }
                        return EMPTY;
                    }),
                )
                .subscribe((translatedMessage: string) => {
                    this.userNotificationService.showError(translatedMessage);
                });
            this.workflowRunQueueService.establishWebsocketConnection();
            this.datasetNotificationService.establishWebsocketConnection();
            // If user is logged in we initially load the numbers and update the sidebar open state
            this.appRootFacadeService.reloadMyModules();
            this.appRootFacadeService.loadModulesTotalCount();
            this.appRootFacadeService.loadSystemModules();

            this.appRootFacadeService.loadSegmentationWorkflows();
            this.appRootFacadeService.loadTrainingWorkflows();

            this.userWorkspaceService
                .update$()
                .pipe(
                    catchError((err: unknown) => {
                        console.error('Failed updating workspace', err);
                        return EMPTY;
                    }),
                )
                .subscribe();
            this.updateSidebarOpenState();
            this.uiThemeService.determineAndSetTheme();
            // Experimental feature + check if user is in dashboard (very important because the IM flow can only be running in a specific location)
            if (this.experimentalFeaturesService.areInlineManualActivated && this.windowRef.nativeWindow.location.href.endsWith('ml-overview')) {
                this.router.events
                    .pipe(
                        tap(event => {
                            if (event instanceof NavigationEnd) {
                                // When the navigation is ended we start to use IM, otherwise, it will not find the element in the page.
                                this.tutorialService.updatePlayer();

                                if (this.userService.userSub != null) {
                                    this.tutorialService.checkIfTutorialAlreadyViewed(this.userService.userSub, '68691');
                                }
                            }
                        }),
                    )
                    .subscribe();
            }

            if (environment.name === 'Production') {
                this.trackStartSession();
            } else {
                return;
            }
        }
        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .pipe(
                tap((event: NavigationEnd) => {
                    // Hide all modal if router navigation was triggered and ended
                    this.modalService.dismissAll();

                    // When maintenance mode is active, navigate to Maintenance page
                    // (except for experimental features, because you should be able to turn it off)
                    this.redirectToMaintenancePage(event);
                }),
            )
            .subscribe();

        // redirect on url change, but ignore query params
        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                pairwise(),
                filter(([prev, current]: [NavigationEnd, NavigationEnd]) => {
                    return prev.url.split('?')[0] !== current.url.split('?')[0];
                }),
                tap(() => this.scrollPageToTop()),
            )
            .subscribe();

        this.breakpointObserverService.isSmallDevice$
            .pipe(
                tap(isSmallDevice => {
                    this.isSmallDevice = isSmallDevice;
                }),
            )
            .subscribe();

        this.breakpointObserverService.isSidebarCollapsed$
            .pipe(
                tap(isSidebarCollapsed => {
                    this.isSideBarCollapsed = isSidebarCollapsed;
                }),
            )
            .subscribe();

        this.workflowRunQueueService.workflowEvent$
            .pipe(
                tap(() => {
                    this.changeDetectorRef.detectChanges();
                }),
            )
            .subscribe();
        this.evaluateSubscriptionEndDate();
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.uiThemeService.determineAndSetTheme();
        });

        this.viewLoaded$$.next('loaded');
    }

    toggleSidebarOpenState($event: boolean): void {
        this.isSidebarOpened = $event;
    }

    private redirectToMaintenancePage(routerEvent: NavigationEnd): void {
        if (this.experimentalFeaturesService.isMaintenanceModeActive && routerEvent.url.indexOf('experimental-features') === -1) {
            // If the user is logged in, log him out first. Then, he will be automatically redirected to the maintenance-page when he redirects to home page.
            if (this.userService.isLoggedIn) {
                this.userService.logout();
            }
            this.router.navigate(['/maintenance']);
        }
    }

    private initDebugNgZone(): void {
        const ngZone = this.injector.get(NgZone);

        ngZone.runOutsideAngular(() => {
            setInterval(() => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const taskTrackingZone = (<any>ngZone)._inner.getZoneWith('TaskTrackingZone');
                if (!taskTrackingZone) {
                    throw new Error("'TaskTrackingZone' zone not found! Have you loaded 'node_modules/zone.js/dist/task-tracking.js'?");
                }
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                let tasks: any[] = taskTrackingZone._properties.TaskTrackingZone.getTasksFor('macroTask');
                tasks = { ...tasks };
                if ([...tasks].length > 0) {
                    // eslint-disable-next-line no-console
                    console.debug('ZONE pending tasks=', tasks);
                } else {
                    // eslint-disable-next-line no-console
                    console.debug('ZONE: No pending tasks.');
                }
            }, 1000);
        });
    }

    private scrollPageToTop(): void {
        const contentBox = document.querySelector('.ng-sidebar__content');
        if (contentBox != null) {
            contentBox.scrollTop = 0;
        }
    }

    private updateSidebarOpenState(): void {
        this.isSidebarOpened = this.userService.isLoggedIn && !this.isSideBarCollapsed;
        this.changeDetectorRef.detectChanges();
    }

    private trackStartSession(): void {
        this.userService.userSubscription$.subscribe(subscription => {
            this.userSubscriptionSubject$$.next(subscription as UserSubscription);
        });

        this.userSubscription$.subscribe(subscription => {
            const userId = this.userService.myInfo?.sub || 'unknown_user';
            const userFirstName = this.userService.myInfo?.firstName;
            const userLastName = this.userService.myInfo?.lastName;
            const userName = userFirstName + ' ' + userLastName;
            const userEmail = this.userService.myInfo?.email;
            const eventKey = `session_started_${userId}`;
            const today = new Date().toISOString().split('T')[0]; // Get the current date in YYYY-MM-DD format
            const lastEventDate = localStorage.getItem(eventKey);

            if (subscription) {
                const subscriptionData = {
                    name: subscription?.id || 'none',
                    'Subscription Type': subscription?.name || 'none',
                    'Subscription Key': subscription?.subscriptionKey || 'none',
                    'Subscription StartDate': subscription?.startDate || 'none',
                    'Subscription EndDate': subscription?.endDate || 'none',
                };

                if (lastEventDate !== today) {
                    this.mixpanelTrackingService.identifyUser(userId, userName, userEmail, environment, subscription);
                    this.mixpanelTrackingService.trackEvent('session_started', { userId, ...subscriptionData });

                    // Store the date when the event was sent
                    localStorage.setItem(eventKey, today);
                } else {
                    console.log(`Event was already sent from ${eventKey} for ${userId}`);
                }
            } else {
                console.log(`No subscription recorded for ${userId}`);
            }
        });
    }
}
